import { useApi } from '@/api';
import {
  CurrencyTooltipChange,
  EntityName,
  InfoIcon,
  KpiSkeleton,
  MultipleKpiCard
} from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  Box,
  Card,
  CryptoIcon,
  Flex,
  FlexProps,
  PercentageValue,
  useIsMobile
} from '@blockanalitica/ui';
import styled, { css } from 'styled-components';

const KpiAPY = styled(Flex)<FlexProps>`
  ${(props) => css`
    font-size: ${props.theme.sizes['1']};
    color: ${props.theme.colors.success};
  `}
`;

export default function StarFarmsKpis({ daysAgo }: { daysAgo: string }) {
  const estimatedAbbrev = useIsMobile() ? 'Est.' : 'Estimated';
  const [data, error] = useApi({
    url: `/farms/stars/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  const isMobile = useIsMobile();

  if (error) {
    throw error;
  }

  if (data && Array.isArray(data)) {
    // Keep active farms on top; sink "Coming soon" (tokens_per_second === 0) to the bottom.
    const sortedFarms = [...data].sort(
      (a, b) =>
        (Number(a.tokens_per_second) === 0 ? 1 : 0) -
        (Number(b.tokens_per_second) === 0 ? 1 : 0)
    );
    return (
      <>
        {sortedFarms.map((farm) => {
          if (Number(farm.tokens_per_second) === 0) {
            return (
              <Flex
                gap={['m', 's']}
                flexDirection={['column', 'row']}
                key={farm.address}>
                <Card
                  display="flex"
                  flexDirection="column"
                  gap="s"
                  flexBasis="50%">
                  <EntityName
                    symbol1={
                      <CryptoIcon
                        name={farm.staking_token_symbol}
                        address={farm.staking_token_address}
                        size="2"
                      />
                    }
                    symbol2={
                      <CryptoIcon
                        name={tokensMapping(farm.rewards_token_symbol).name}
                        address={
                          tokensMapping(farm.rewards_token_symbol).address
                        }
                        size="2"
                      />
                    }
                    fontSize="1"
                    color="text">
                    Supply {farm.staking_token_symbol} to get{' '}
                    {tokensMapping(farm.rewards_token_symbol).name} rewards
                  </EntityName>
                  <Flex color="muted" alignItems="center" gap="2xs">
                    <InfoIcon size="1" />
                    <Flex gap="3xs">Coming soon</Flex>
                  </Flex>
                </Card>
              </Flex>
            );
          }

          const farmFooter = [
            {
              title: 'Total Staked',
              value: (
                <CurrencyTooltipChange
                  field="total_staked"
                  data={farm}
                  options={{
                    currency: tokensMapping(farm.staking_token_symbol),
                    iconSize: '0'
                  }}
                />
              )
            },
            {
              title: `Total ${estimatedAbbrev} ${farm.rewards_token_symbol} Rewards`,
              value: (
                <CurrencyTooltipChange
                  field="total_farmed"
                  data={farm}
                  options={{
                    currency: {
                      name: farm.rewards_token_symbol,
                      address: farm.rewards_token_address
                    },
                    iconSize: '0'
                  }}
                />
              )
            }
          ];

          if (!isMobile) {
            farmFooter.push({
              title: 'Suppliers',
              value: <CurrencyTooltipChange field="depositors" data={farm} />
            });
          }

          return (
            <MultipleKpiCard
              key={farm.address}
              linkTo={farm.address}
              title={
                <Flex justifyContent="space-between" flex="1">
                  <Flex gap="s" alignItems="flex-end">
                    <EntityName
                      symbol1={
                        <CryptoIcon
                          name={farm.staking_token_symbol}
                          address={farm.staking_token_address}
                          size="2"
                        />
                      }
                      symbol2={
                        <CryptoIcon
                          name={tokensMapping(farm.rewards_token_symbol).name}
                          address={
                            tokensMapping(farm.rewards_token_symbol).address
                          }
                          size="2"
                        />
                      }
                      fontSize="1"
                      color="text">
                      Supply {farm.staking_token_symbol} to get{' '}
                      {tokensMapping(farm.rewards_token_symbol).name} rewards
                    </EntityName>
                  </Flex>
                  <KpiAPY alignItems="center" gap="3xs">
                    <Box color="muted">APY:</Box>
                    <PercentageValue
                      color="white"
                      value={farm.apy}
                      options={{ tooltip: false }}
                    />
                  </KpiAPY>
                </Flex>
              }
              footer={farmFooter}
            />
          );
        })}
      </>
    );
  }

  return <KpiSkeleton />;
}
